import React, { useRef, useEffect, useState } from "react";
import Renderer from "../renderer";
import { generateRandomColor, generateUUID } from '../../utils';
import { drawLine } from "../../utils";
import { Button, Menu, MenuItem } from "@mui/material";
import quizPlayIcon from "../../assets/images/quiz/speech-play.svg";
import quizPauseIcon from "../../assets/images/quiz/speech-pause.svg";
import quizReplayIcon from "../../assets/images/quiz/speech-retry.svg";
import speechLoadingIcon from "../../assets/images/quiz/speech-loading.gif";
import { toast } from 'react-toastify';
import { getSpeechConfig, textToSpeech } from "../../store/quiz/quizBaseApi";

const validRelations = ['one-to-one', 'one-to-many', 'many-to-one', 'many-to-many'];

export default function MatchTheFollowing({ right, left, style, mode, relation, questionId, disabled, selectedAnswer, handleAnswerSelection, enableTextToAudio, preview, quizPart, evaluated, question }) {

    const student = JSON.parse(localStorage.getItem('profile')) || {};
    const answerMap = useRef(left.reduce((acc, obj) => {
        acc[obj['label']] = { labels: [], color: generateRandomColor() };
        return acc;
    }, {}));


    const list = useRef();
    const selected = useRef({ lhs: null, rhs: null });

    const [contextMenu, setContextMenu] = useState(null);
    const [contextData, setContextData] = useState(null);

    // Function to handle right-click context menu
    const handleContextMenu = (event, side, label) => {
        event.preventDefault();

        // Check if the element is matched
        if (event.target.parentNode.getAttribute('matched') === 'true') {
            setContextData({ side, label });
            setContextMenu({
                mouseX: event.clientX - 2,
                mouseY: event.clientY - 4,
            });
        }
    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const handleRemoveMatch = () => {
        if (contextData) {
            removeMatch(contextData.side, contextData.label);
        }
        handleClose();
    };

    // Function to remove a match
    const removeMatch = (side, label) => {
        const id = questionId + '-' + label;
        const element = document.getElementById(id);

        if (!element) return;

        let matchedLabels = [];

        if (side === 'lhs') {
            matchedLabels = [...(answerMap.current[label]?.labels || [])]; // Get matched RHS labels
        } else if (side === 'rhs') {
            matchedLabels = Object.keys(answerMap.current).filter(lhsKey => answerMap.current[lhsKey].labels.includes(label));
        }

        // Remove the corresponding connection lines
        document.querySelectorAll('.line').forEach(line => {
            if (line.getAttribute('data-stem') === id || line.getAttribute('data-option') === id) {
                line.remove();
            }
        });

        // Remove the match from answerMap
        if (side === 'lhs') {
            answerMap.current[label].labels = [];
        } else {
            Object.keys(answerMap.current).forEach(lhsKey => {
                answerMap.current[lhsKey].labels = answerMap.current[lhsKey].labels.filter(rhsLabel => rhsLabel !== label);
            });
        }

        // **Check if the LHS element should keep its color**
        if (side === 'lhs') {
            const lhsElement = document.getElementById(id);
            if (lhsElement && answerMap.current[label]?.labels.length === 0) {
                lhsElement.parentNode.style.backgroundColor = '#F3F8FB'; // Reset only if fully unmatched
            }
        }

        // **Check if the RHS element should keep its color**
        if (side === 'rhs') {
            const rhsElement = document.getElementById(id);
            const stillMatched = Object.values(answerMap.current).some(entry => entry.labels.includes(label));
            if (rhsElement && !stillMatched) {
                rhsElement.parentNode.style.backgroundColor = '#F3F8FB'; // Reset only if fully unmatched
            }
        }

        // **Ensure LHS retains color if still matched**
        matchedLabels.forEach(rhsLabel => {
            const rhsId = questionId + '-' + rhsLabel;
            const rhsElement = document.getElementById(rhsId);
            if (rhsElement) {
                const stillMatched = Object.values(answerMap.current).some(entry => entry.labels.includes(rhsLabel));
                if (!stillMatched) {
                    rhsElement.parentNode.style.backgroundColor = '#F3F8FB';
                }
            }
        });

        matchedLabels.forEach(lhsLabel => {
            const lhsId = questionId + '-' + lhsLabel;
            const lhsElement = document.getElementById(lhsId);
            if (lhsElement) {
                if (answerMap.current[lhsLabel]?.labels.length === 0) {
                    lhsElement.parentNode.style.backgroundColor = '#F3F8FB';
                } else {
                    lhsElement.parentNode.style.backgroundColor = answerMap.current[lhsLabel]?.color; // Keep color if still matched
                }
            }
        });

        // Reset selected states
        selected.current.lhs = null;
        selected.current.rhs = null;

        submitOnComplete();
    };


    // Add context menu event listeners to LHS and RHS elements
    useEffect(() => {
        if (list.current) {
            const lhsElements = list.current.querySelectorAll('.lhs');
            const rhsElements = list.current.querySelectorAll('.rhs');

            lhsElements.forEach(el => {
                const label = el.querySelector('input').id.split('-').pop();
                el.addEventListener('contextmenu', (event) => handleContextMenu(event, 'lhs', label));
            });

            rhsElements.forEach(el => {
                const label = el.querySelector('input').id.split('-').pop();
                el.addEventListener('contextmenu', (event) => handleContextMenu(event, 'rhs', label));
            });

            return () => {
                lhsElements.forEach(el => el.removeEventListener('contextmenu', handleContextMenu));
                rhsElements.forEach(el => el.removeEventListener('contextmenu', handleContextMenu));
            };
        }
    }, [left, right]);

    const submitOnComplete = () => {
        let completed = Object.values(answerMap.current).filter(e => e.labels.length);

        if (handleAnswerSelection) {
            handleAnswerSelection({ target: answerMap.current });
        }
    }

    const updateMatch = (target, side) => {
        list.current.querySelectorAll(`li [data-side="${side}"]`).forEach(el => {
            if (String(target.id) === String(el.id)) {
                selected.current[side] = el;
            }
        });
    }

    const reloadMatches = () => {
        setTimeout(() => {
            Object.keys(selectedAnswer).forEach(key => {
                if (selectedAnswer[key].labels) {
                    selectedAnswer[key].labels.forEach(label => {
                        let isCorrect = selectedAnswer[key]['correct'];
                        let id = questionId + '-';

                        let stem = document.getElementById(id + key),
                            target = document.getElementById(id + label),
                            color = selectedAnswer[key]['color'],
                            lineColor = selectedAnswer[key]['lineColor'];

                        if (stem && target) {
                            // Force recalculation of element positions
                            const stemRect = stem.getBoundingClientRect();
                            const targetRect = target.getBoundingClientRect();

                            drawLine(stem, target, (lineColor || color), !isCorrect);

                            [stem.parentNode, target.parentNode].forEach(node => {
                                node.style.backgroundColor = color;
                                node.setAttribute('matched', 'true');
                            });
                        }

                        if (!isCorrect) {
                            let answerLabels = selectedAnswer[key]['answerLabels'];
                            displayMTFCorrectAnswers(answerLabels, key);
                        }
                    });
                } else {
                    let answerLabels = selectedAnswer[key]['answerLabels'];
                    displayMTFCorrectAnswers(answerLabels, key);
                }
            });
        }, 100); // Small delay to ensure proper rendering
    }

    const displayMTFCorrectAnswers = (answerLabels, key) => {
        setTimeout(() => {
            let id = questionId + '-';
            let lineColor = 'lightgreen',
                stem = document.getElementById(id + key),
                color = generateRandomColor();
            if (stem) {
                stem.parentNode.style.backgroundColor = color;
                if (answerLabels && answerLabels.length) {
                    answerLabels.forEach(item => {
                        let elem = document.getElementById(id + item);
                        if (elem) {
                            elem.parentNode.style.backgroundColor = color;
                            drawLine(stem, elem, lineColor);
                        }
                    });
                }
            }
        }, 100);
    }

    const canMatch = (target, data) => {
        relation = String(relation).toLowerCase();
        let id = String(target.id).split('-').pop();

        if (!relation) {
            relation = 'one-to-one';
        }
        if (!validRelations.includes(relation)) {
            relation = 'one-to-one';
        }

        if (relation === 'one-to-one') {
            if (answerMap.current[data.label] && answerMap.current[data.label]['labels'].length > 0) {
                return false;
            }
            let rhsMatched = Object.values(answerMap.current).some(entry => entry.labels.includes(id));
            if (rhsMatched) {
                return false; // RHS already has a match
            }
        }

        if (relation === 'one-to-many') {
            // for (const key in answerMap.current) {
            //     if (answerMap.current[key]['labels'].includes(id)) {
            //         return false; // Exit early if condition is met
            //     }
            // }

            // LHS can have multiple matches, but RHS should have only one match
            let rhsMatched = Object.values(answerMap.current).some(entry => entry.labels.includes(id));
            if (rhsMatched) {
                return false; // RHS already matched
            }
        }

        if (relation === 'many-to-one') {
            // if (!selected.current.lhs) {
            //     return false;
            // }
            // let label = selected.current.lhs.getAttribute('id');
            // label = String(label).split('-').pop()

            // if (!answerMap.current[label]) {
            //     return false;
            // }

            // if (answerMap.current[label]['labels'].length) {
            //     return false;
            // }

            if (!selected.current.lhs) {
                return false;
            }
            let lhsLabel = selected.current.lhs.getAttribute('id').split('-').pop();
            if (answerMap.current[lhsLabel] && answerMap.current[lhsLabel]['labels'].length > 0) {
                return false; // LHS already has a match
            }
        }

        // Default: many-to-many/can match, return true
        return true;
    }

    const handleElementSelect = ({ target }) => {
        if (disabled) return;

        const data = JSON.parse(target.getAttribute('data') || '{}');
        const side = target.getAttribute('data-side');

        if (String(target.parentNode.tagName).toLowerCase() !== 'label') return;

        updateMatch(target, side);

        if (!canMatch(target, data)) {
            selected.current.lhs = null;
            selected.current.rhs = null;
            return;
        }

        if ((!answerMap.current[data?.label] || !answerMap.current[data.label]['labels'].length)) {
            list.current.querySelectorAll('li .' + side).forEach(el => el.removeAttribute('selected'));
            target.parentNode.setAttribute('selected', 'true');
        }

        if (selected.current.lhs && selected.current.rhs) {
            let lhs = selected.current.lhs.id,
                rhs = selected.current.rhs.id;

            let lhsLabel = lhs.split('-').pop();

            let curr = { ...answerMap.current[lhsLabel], labels: [...(answerMap.current[lhsLabel]?.labels || [])] };

            // let curr = answerMap.current[lhsLabel],
            let stem = document.getElementById(lhs),
                target = document.getElementById(rhs),
                color = answerMap.current[lhsLabel]['color'];

            curr.labels.push(String(rhs).split('-').pop());
            answerMap.current[lhsLabel] = curr;

            if (stem && target) {
                drawLine(stem, target, color);
            }

            [stem.parentNode, target.parentNode].forEach(node => {
                node.style.backgroundColor = color;
                node.setAttribute('matched', 'true');
            });

            selected.current.lhs = null;
            selected.current.rhs = null;

            submitOnComplete();
        }
    }

    useEffect(() => {
        if (evaluated) {
            reloadMatches();
        } else {
            // Object.keys(selectedAnswer || {}).forEach((key) => {
            //     if (!answerMap.current[key]) {
            //         answerMap.current[key] = {}; // Initialize if missing
            //     }
            //     answerMap.current[key] = {
            //         labels: selectedAnswer[key]?.labels || [],
            //         color: selectedAnswer[key]?.color || "rgba(255, 199, 74, 0.25)",
            //     };
            // });
        }
        // eslint-disable-next-line
    }, [evaluated]);

    useEffect(() => {
        if (selectedAnswer && Object.keys(selectedAnswer).length > 0) {

            const clonedAnswerMap = { ...answerMap.current };

            Object.keys(selectedAnswer[question] || {}).forEach((key) => {
                clonedAnswerMap[key] = {
                    labels: selectedAnswer[question][key]?.labels || [],
                    color: selectedAnswer[question][key]?.color || "rgba(255, 199, 74, 0.25)",
                };
                // 🔹 Redraw lines and update UI
                if (selectedAnswer[question][key]?.labels) {
                    selectedAnswer[question][key].labels.forEach(label => {
                        let id = questionId + '-';
                        let stem = document.getElementById(id + key);
                        let target = document.getElementById(id + label);
                        let color = selectedAnswer[question][key]?.color;

                        if (stem && target) {
                            const existingLine = document.querySelector(`.line[data-stem="${id + key}"][data-option="${id + label}"]`);
                            if (!existingLine) {
                                drawLine(stem, target, color);
                                [stem.parentNode, target.parentNode].forEach(node => {
                                    node.style.backgroundColor = color;
                                    node.setAttribute('matched', 'true');
                                });
                            }
                        }
                    });
                }
            });
            answerMap.current = clonedAnswerMap;
        } else {
            answerMap.current = left.reduce((acc, obj) => {
                acc[obj['label']] = { labels: [], color: generateRandomColor() };
                return acc;
            }, {})
        }
    }, [right, left, selectedAnswer]);

    let greaterSide = left.length > right.length ? 'left' : 'right';
    let side = greaterSide == 'left' ? left : right;

    const fetchAudio = async (content, setAudioUrl, setIsLoading) => {
        setIsLoading(true);
        try {
            let conf = await getSpeechConfig(String(student.geographicalType).toLowerCase(), student.learningGeneration);
            const data = await textToSpeech({ text: content, ...conf });

            if (data && data.response && data.response.audio) {
                // setAudioUrl(data.response.audio);
                // localStorage.setItem(String(content).trim(), data.response.audio);
                // new s3 endpoint change --- minimal fix for audio play
                const correctedAudioUrl = data.response.audio.replace(
                    'knowledgebasechat.s3.eu-west-1.amazonaws.com',
                    'knowledgebasechat.s3-us-west-1.amazonaws.com'
                );
                setAudioUrl(correctedAudioUrl);
                localStorage.setItem(String(content).trim(), correctedAudioUrl);
            }
        } catch (error) {
            const message = error.response ? error.response.data.info.detail : error.message;
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const PlayIcon = () => <img style={{ width: '70%' }} src={quizPlayIcon} />
    const PauseIcon = () => <img style={{ width: '70%' }} src={quizPauseIcon} />
    const RetryIcon = () => <img style={{ width: '70%' }} src={quizReplayIcon} />
    const LoadingIcon = () => <img style={{ width: '70%' }} height={'50px'} src={speechLoadingIcon} />

    useEffect(() => {
        // Set equal heights for each pair of left and right elements
        if (list.current) {
            const listItems = list.current.querySelectorAll('li');
            listItems.forEach(item => {
                const lhs = item.querySelector('.lhs');
                const rhs = item.querySelector('.rhs');
                if (lhs && rhs) {
                    const maxHeight = Math.max(lhs.offsetHeight, rhs.offsetHeight);
                    lhs.style.height = `${maxHeight}px`;
                    rhs.style.height = `${maxHeight}px`;
                }
            });
        }
    }, [left, right]);

    return (
        // <div style={{...styles.container, ...style}}>
        //     <ul className="questions" style={styles.list} ref={list}>
        //         {side.map((item, index) => {
        //             let leftSide = side == 'left' ? item : left[index];
        //             let rightSide = side == 'right' ? item : right[index];

        //             let isCorrectAns = rightSide.correct; // For rendering the answers
        //             let color = isCorrectAns ? 'green' : 'red';
        //             return (
        //                 <li key={questionId + String(index)} style={styles.item}>
        //                     <label className={leftSide ? (disabled ?  'answer-view' : 'answer-item') + " lhs" : ''} style={{...styles.renderer, minWidth: '160px', minHeight:'80px', maxWidth: '38%'}}>
        //                         {leftSide && <>
        //                             <input value={true} id={questionId + '-' + leftSide.label} style={styles.input} data={JSON.stringify(leftSide)} data-side="lhs" onClick={handleElementSelect} type="radio"/>
        //                             <Renderer
        //                                 content={leftSide?.value?.text}
        //                                 contentType={leftSide.value?.style}
        //                                 contentUrl={leftSide.value?.contentURL}
        //                                 templateType={leftSide.value?.displayTemplate}
        //                                 mediaStyle={styles.media}
        //                                 overrideHTML={leftSide.value?.overrideHTML}
        //                             />
        //                         </>}
        //                     </label>
        //                     <label className={(disabled ?  'answer-view' : 'answer-item') + " rhs"} style={{minWidth: '160px', height:'40px', padding: '5px', borderRadius: '5px', backgroundColor: 'var(--mui-palette-background-level2)', position: 'relative', display: 'flex', alignItems: 'center', color: 'var(--mui-palette-primary-main)', fontWeight: 'bold'}}>
        //                         <input id={questionId + '-' + rightSide.label} style={styles.input} data={JSON.stringify(leftSide)} data-side="rhs" onClick={handleElementSelect} type="radio" />
        //                         <Renderer
        //                             data={JSON.stringify(rightSide)}
        //                             onClick={handleElementSelect}
        //                             content={rightSide?.value?.text}
        //                             contentType={rightSide.value?.style}
        //                             contentUrl={rightSide.value?.contentURL}
        //                             templateType={rightSide.value?.displayTemplate || 'side-by-side'}
        //                             overrideHTML={rightSide.value?.overrideHTML}
        //                         />

        //                         {mode === 'result' ? <div style={{...styles.correctAnswerContainer, border: '2px solid ' + color}}>
        //                             <div style={{...styles.correctAnswer, color: color}}>
        //                                 {isCorrectAns ? (<span>&#10004;</span>) : (<span>&#10007;</span>)}
        //                             </div>
        //                         </div> : ''}
        //                     </label>
        //                 </li>
        //             )
        //         })}
        //     </ul>
        // </div>

        <div style={{ ...styles.container, ...style }}>
            <Menu
                open={Boolean(contextMenu)}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleRemoveMatch}>Remove Match</MenuItem>
            </Menu>
            <ul className="questions" style={styles.list} ref={list}>
                {side.map((item, index) => {
                    // Left side audio states
                    const [leftAudioUrl, setLeftAudioUrl] = React.useState(null);
                    const [isLeftLoading, setIsLeftLoading] = React.useState(false);
                    const [isLeftPlaying, setIsLeftPlaying] = React.useState(false);
                    const leftAudioRef = React.useRef(null);

                    // Right side audio states
                    const [rightAudioUrl, setRightAudioUrl] = React.useState(null);
                    const [isRightLoading, setIsRightLoading] = React.useState(false);
                    const [isRightPlaying, setIsRightPlaying] = React.useState(false);
                    const rightAudioRef = React.useRef(null);

                    let leftSide = side == 'left' ? item : left[index];
                    let rightSide = side == 'right' ? item : right[index];

                    let isCorrectAns = rightSide?.correct; // For rendering the answers
                    let color = isCorrectAns ? 'green' : 'red';

                    const handleLeftPlayPause = () => {
                        if (!leftAudioRef.current || !leftAudioRef.current.src) {
                            console.error("Audio element or source not available");
                            return;
                        }
                        if (isLeftPlaying) {
                            leftAudioRef.current.pause();
                        } else {
                            leftAudioRef.current.play();
                        }
                    };

                    const handleRightPlayPause = () => {
                        if (!rightAudioRef.current || !rightAudioRef.current.src) {
                            console.error("Audio element or source not available");
                            return;
                        }
                        if (isRightPlaying) {
                            rightAudioRef.current.pause();
                        } else {
                            rightAudioRef.current.play();
                        }
                    };

                    React.useEffect(() => {
                        // Fetch left-side audio if not cached
                        const existingLeftAudio = localStorage.getItem(String(leftSide?.value?.text).trim());
                        if (existingLeftAudio) {
                            setLeftAudioUrl(existingLeftAudio);
                        } else if (leftSide?.value?.text) {
                            fetchAudio(leftSide?.value?.text, setLeftAudioUrl, setIsLeftLoading);
                        }

                        // Fetch right-side audio if not cached
                        const existingRightAudio = localStorage.getItem(String(rightSide?.value?.text).trim());

                        if (existingRightAudio) {
                            setRightAudioUrl(existingRightAudio);
                        } else if (rightSide?.value?.text) {
                            fetchAudio(rightSide.value?.text, setRightAudioUrl, setIsRightLoading);
                        }
                    }, [leftSide?.value?.text, rightSide?.value?.text]);

                    return (
                        <li key={questionId + String(index)} style={styles.item}>
                            <div style={{ display: 'flex' }}>
                                {leftSide?.value?.text && enableTextToAudio &&
                                    <Button size={'small'} type={'secondary'} style={{ ...styles.button, }}
                                        onClick={!leftAudioUrl ? () => fetchAudio(leftSide.value?.text, setLeftAudioUrl, setIsLeftLoading) : handleLeftPlayPause}
                                        disabled={isLeftLoading}>{isLeftLoading ? <LoadingIcon /> : isLeftPlaying ? <PauseIcon /> : <PlayIcon />}
                                    </Button>
                                }
                                <label className={leftSide ? (disabled ? 'answer-view' : 'answer-item') + " lhs" : ''} style={{ ...styles.sideLabel, paddingLeft: '5px', justifyContent: 'flex-start' }}>
                                    {leftSide && <>
                                        <span className='option-label' style={styles.textIndex} >{index + 1}.</span>
                                        <input value={true} id={questionId + '-' + leftSide.label} style={styles.input} data={JSON.stringify(leftSide)} data-side="lhs" onClick={handleElementSelect} type="radio" />
                                        <Renderer
                                            content={leftSide?.value?.text}
                                            contentType={leftSide.value?.style}
                                            contentUrl={leftSide.value?.contentURL}
                                            templateType={leftSide.value?.displayTemplate}
                                            mediaStyle={styles.media}
                                            overrideHTML={leftSide.value?.overrideHTML}
                                            quizPart={quizPart}
                                            mtf={true}
                                        />
                                    </>}
                                </label>
                                {enableTextToAudio && (
                                    <audio
                                        ref={leftAudioRef}
                                        src={leftAudioUrl}
                                        onPlay={() => setIsLeftPlaying(true)}
                                        onPause={() => setIsLeftPlaying(false)}
                                        onEnded={() => setIsLeftPlaying(false)}
                                        onError={(e) => console.error("Audio error:", e)}
                                    />
                                )}
                            </div>
                            <div style={{ display: 'flex' }}>
                                <label className={(disabled ? 'answer-view' : 'answer-item') + " rhs"}
                                    // style={{ minWidth: '160px', height: '40px', padding: '5px', borderRadius: '5px', backgroundColor: 'var(--mui-palette-background-level2)', position: 'relative', display: 'flex', alignItems: 'center', color: 'var(--mui-palette-primary-main)', fontWeight: 'bold' }}
                                    style={{ ...styles.sideLabel, paddingLeft: '5px', justifyContent: 'flex-start' }}
                                >
                                    <span className='option-label' style={styles.textIndex} >{index + 1}.</span>
                                    <input id={questionId + '-' + rightSide?.label} style={styles.input} data={JSON.stringify(rightSide)} data-side="rhs" onClick={handleElementSelect} type="radio" />
                                    <Renderer
                                        data={JSON.stringify(rightSide)}
                                        // onClick={handleElementSelect}
                                        content={rightSide?.value?.text}
                                        contentType={rightSide?.value?.style}
                                        contentUrl={rightSide?.value?.contentURL}
                                        templateType={rightSide?.value?.displayTemplate || 'side-by-side'}
                                        overrideHTML={rightSide?.value?.overrideHTML}
                                        quizPart={quizPart}
                                        mtf={true}

                                    />
                                    {!preview && mode === 'result' ? <div style={{ ...styles.correctAnswerContainer, border: '2px solid ' + color }}>
                                        <div style={{ ...styles.correctAnswer, color: color }}>
                                            {isCorrectAns ? (<span>&#10004;</span>) : (<span>&#10007;</span>)}
                                        </div>
                                    </div> : ''}
                                </label>
                                {rightSide?.value?.text && enableTextToAudio &&
                                    <Button size={'small'} type={'secondary'} style={{ ...styles.button, }}
                                        onClick={!rightAudioUrl ? () => fetchAudio(rightSide.value?.text, setRightAudioUrl, setIsRightLoading) : handleRightPlayPause}
                                        disabled={isRightLoading}>{isRightLoading ? <LoadingIcon /> : isRightPlaying ? <PauseIcon /> : <PlayIcon />}
                                    </Button>
                                }
                                {enableTextToAudio && (
                                    <audio
                                        ref={rightAudioRef}
                                        src={rightAudioUrl}
                                        onPlay={() => setIsRightPlaying(true)}
                                        onPause={() => setIsRightPlaying(false)}
                                        onEnded={() => setIsRightPlaying(false)}
                                        onError={(e) => console.error("Audio error:", e)}
                                    />
                                )}
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div >
    )
}

const styles = {
    container: {
        // maxWidth: '500px',
        // margin: '20px 0 40px',
        // display: 'block',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%'
    },
    list: {
        paddingLeft: 0,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '70%'
    },
    renderer: {
        display: 'block',
        padding: '3px',
        position: 'relative'
    },
    media: {
        // minWidth: '150px',
        // objectFit: 'contain'
    },
    item: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // marginTop: '10px',
        // marginBottom: '10px',
        gap: '10px',
    },
    input: {
        appearance: 'none',
        position: 'absolute',
        width: '100%',
        height: '100%',
        cursor: 'pointer'
    },
    correctAnswerContainer: {
        position: 'absolute',
        height: '30px',
        width: '30px',
        top: '14px',
        right: '-56px',
        borderRadius: '20px',
        border: '2px solid green'
    },
    correctAnswer: {
        color: 'green',
        fontSize: '22px',
        paddingLeft: '6px',
        paddingTop: '4px',
    },
    hearButtonContainer: {
        display: 'flex',
        alignItems: 'start'
    },
    sideLabel: {
        width: '250px', // Set a fixed width for both left and right labels
        height: 'auto', // Allow height to adjust based on content
        minHeight: '30px', // Minimum height for uniformity
        padding: 3,
        borderRadius: '5px',
        backgroundColor: '#F3F8FB',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    textIndex: {
        // position: "absolute",
        // left: "0",
        // top: "0",
        backgroundColor: "transparent",
        color: "var(--mui-palette-common-black)",
        width: "40px",
        height: "98%",
        textAlign: "center",
        border: "1px solid #CDE6F5",
        borderRadius: "4px",
        fontSize: "20px",
        // zIndex: "2",
        marginRight: '1rem',
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
}